import api from "@/config/api"
import { isPushSupported } from "@/push/registerSW"

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
  const rawData = window.atob(base64)
  const output = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i += 1) {
    output[i] = rawData.charCodeAt(i)
  }
  return output
}

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer)
  let binary = ""
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }
  return window.btoa(binary)
}

const extractKeys = (subscription) => {
  const p256dh = arrayBufferToBase64(subscription.getKey("p256dh"))
  const auth = arrayBufferToBase64(subscription.getKey("auth"))
  return { p256dh, auth }
}

export default {
  namespaced: true,
  state: () => ({
    supported: isPushSupported(),
    permission: isPushSupported() ? Notification.permission : "default",
    subscribed: false,
    vapidPublicKey: null,
    loading: false
  }),
  mutations: {
    setSupported(state, value) { state.supported = value },
    setPermission(state, value) { state.permission = value },
    setSubscribed(state, value) { state.subscribed = value },
    setVapidPublicKey(state, value) { state.vapidPublicKey = value },
    setLoading(state, value) { state.loading = value }
  },
  actions: {
    async fetchVapidKey({ state, commit }) {
      if (state.vapidPublicKey) return state.vapidPublicKey
      const response = await api.get("/push_subscriptions/vapid_public_key")
      commit("setVapidPublicKey", response.data.public_key)
      return response.data.public_key
    },

    async sync({ state, commit }) {
      if (!state.supported) return
      commit("setPermission", Notification.permission)

      const registration = await navigator.serviceWorker.getRegistration()
      if (!registration) {
        commit("setSubscribed", false)
        return
      }

      const subscription = await registration.pushManager.getSubscription()
      commit("setSubscribed", !!subscription)
    },

    async subscribe({ state, commit, dispatch }) {
      if (!state.supported) throw new Error("push_unsupported")

      const permission = await Notification.requestPermission()
      commit("setPermission", permission)
      if (permission !== "granted") throw new Error("permission_denied")

      const publicKey = await dispatch("fetchVapidKey")
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      })

      const { p256dh, auth } = extractKeys(subscription)
      await api.post("/push_subscriptions", {
        endpoint: subscription.endpoint,
        p256dh,
        auth,
        user_agent: navigator.userAgent
      })

      commit("setSubscribed", true)
      return subscription
    },

    async unsubscribe({ state, commit }) {
      if (!state.supported) return

      const registration = await navigator.serviceWorker.getRegistration()
      const subscription = registration && await registration.pushManager.getSubscription()
      if (!subscription) {
        commit("setSubscribed", false)
        return
      }

      await api.delete("/push_subscriptions/by_endpoint", { params: { endpoint: subscription.endpoint } })
      await subscription.unsubscribe()
      commit("setSubscribed", false)
    }
  }
}
